// Case 3 in snippets/hero.liquid renders a desktop AND a mobile <video> into the
// same document and lets CSS hide one of them (.wt-mobile-hidden /
// .wt-desktop-hidden -> display:none either side of 1200px).
//
// display:none does not stop a <video> from downloading its body or playing it.
// A Lighthouse mobile run pulled both the 3.3 MB mobile file and the 8.8 MB
// desktop file — 70% of total page weight — for a video no phone user can see.
//
// hero.liquid withholds autoplay when both are present; this detaches the
// variant the current viewport will not show and starts the one it will. The
// detached node is kept rather than destroyed (a detached <video> issues no
// requests) and swapped back if the viewport crosses the breakpoint, so
// narrowing a desktop window still ends up with a playing video.
(() => {
  if (window.__responsiveVideoInit) return;
  window.__responsiveVideoInit = true;

  const DESKTOP_ONLY = "wt-mobile-hidden"; // shown at >=1200px
  const MOBILE_ONLY = "wt-desktop-hidden"; // shown below 1200px

  /** <video-controls> carries the same visibility class, so move it as one unit. */
  const unitFor = (video) => {
    const parent = video.parentElement;
    return parent && parent.tagName === "VIDEO-CONTROLS" ? parent : video;
  };

  const play = (video, wantsAutoplay) => {
    if (!wantsAutoplay) return;
    video.muted = true;
    video.playsInline = true;
    const started = video.play();
    // Autoplay can still be refused. Nothing useful to do, but an unhandled
    // rejection would surface as a console error.
    if (started && typeof started.catch === "function") started.catch(() => {});
  };

  const setup = (container) => {
    const minWidth = parseInt(container.dataset.desktopMinWidth, 10) || 1200;
    const wantsAutoplay = container.dataset.autoplay !== "false";
    const query = window.matchMedia(`(min-width: ${minWidth}px)`);

    const videos = {
      desktop: container.querySelector(`video.${DESKTOP_ONLY}`),
      mobile: container.querySelector(`video.${MOBILE_ONLY}`),
    };
    if (!videos.desktop || !videos.mobile) return;

    // Comment nodes hold each element's slot while it sits detached.
    const anchors = {};
    Object.keys(videos).forEach((key) => {
      const unit = unitFor(videos[key]);
      anchors[key] = document.createComment(`responsive-video:${key}`);
      unit.parentNode.insertBefore(anchors[key], unit);
    });

    const apply = () => {
      const keep = query.matches ? "desktop" : "mobile";
      const drop = query.matches ? "mobile" : "desktop";

      const dropUnit = unitFor(videos[drop]);
      if (dropUnit.parentNode) {
        videos[drop].pause();
        dropUnit.remove();
      }

      const keepUnit = unitFor(videos[keep]);
      if (!keepUnit.parentNode) {
        anchors[keep].parentNode.insertBefore(
          keepUnit,
          anchors[keep].nextSibling,
        );
      }
      play(videos[keep], wantsAutoplay);
    };

    apply();
    query.addEventListener("change", apply);
  };

  const init = () =>
    document.querySelectorAll("[data-responsive-video]").forEach(setup);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
